/**
 * @file unit-conversions.ts
 * @description Stateless unit conversion functions for temperature and pressure
 */

import { TemperatureUnit, PressureUnit, TemperatureValue, PressureValue } from '../types';

/**
 * Converts temperature to Kelvin from any supported unit
 * @param tempValue The temperature value and unit to convert
 * @returns The temperature in Kelvin
 */
export function convertTempToK(tempValue: TemperatureValue): TemperatureValue {
  switch (tempValue.temperatureUnit) {
    case TemperatureUnit.K: return { temperature: tempValue.temperature, temperatureUnit: TemperatureUnit.K };
    case TemperatureUnit.C: return { temperature: tempValue.temperature + 273.15, temperatureUnit: TemperatureUnit.K };
    case TemperatureUnit.F: return { temperature: (tempValue.temperature + 459.67) * 5 / 9, temperatureUnit: TemperatureUnit.K };
    default: throw new Error('Unsupported temperature unit');
  }
}

/**
 * Converts temperature from Kelvin to any supported unit
 * @param tempK The temperature value in Kelvin
 * @param targetUnit The target unit
 * @returns The temperature in the target unit
 */
export function convertTempFromK(tempK: number, targetUnit: TemperatureUnit): TemperatureValue {
  switch (targetUnit) {
    case TemperatureUnit.K: return { temperature: tempK, temperatureUnit: TemperatureUnit.K };
    case TemperatureUnit.C: return { temperature: tempK - 273.15, temperatureUnit: TemperatureUnit.C };
    case TemperatureUnit.F: return { temperature: tempK * 9 / 5 - 459.67, temperatureUnit: TemperatureUnit.F };
    default: throw new Error('Unsupported temperature unit');
  }
}

/**
 * Converts temperature difference from Kelvin to any supported unit
 * @param deltaTempK The temperature difference in Kelvin
 * @param targetUnit The target unit
 * @returns The temperature difference in the target unit
 */
export function convertDeltaTempFromK(deltaTempK: number, targetUnit: TemperatureUnit): TemperatureValue {
  switch (targetUnit) {
    case TemperatureUnit.K: return { temperature: deltaTempK, temperatureUnit: TemperatureUnit.K };
    case TemperatureUnit.C: return { temperature: deltaTempK, temperatureUnit: TemperatureUnit.C };
    case TemperatureUnit.F: return { temperature: deltaTempK * 9 / 5, temperatureUnit: TemperatureUnit.F };
    default: throw new Error('Unsupported temperature unit');
  }
}

/**
 * Converts pressure to Pascals (absolute) from any supported unit
 * @param pressureValue The pressure value and unit to convert
 * @returns The pressure in Pascals (absolute)
 */
export function convertPressureToPa(pressureValue: PressureValue, ATMOSPHERIC_PRESSURE_PA: number = 101325): PressureValue {
  
  switch (pressureValue.pressureUnit) {
    // Absolute pressure units - convert to Pa
    case PressureUnit.PAA: return { pressure: pressureValue.pressure, pressureUnit: PressureUnit.PAA };
    case PressureUnit.KPAA: return { pressure: pressureValue.pressure * 1000, pressureUnit: PressureUnit.PAA };
    case PressureUnit.BARA: return { pressure: pressureValue.pressure * 100000, pressureUnit: PressureUnit.PAA };
    case PressureUnit.PSIA: return { pressure: pressureValue.pressure * 6894.76, pressureUnit: PressureUnit.PAA };
    
    // Gauge pressure units - convert to Pa and add atmospheric pressure
    case PressureUnit.PAG: return { pressure: pressureValue.pressure + ATMOSPHERIC_PRESSURE_PA, pressureUnit: PressureUnit.PAA };
    case PressureUnit.KPAG: return { pressure: pressureValue.pressure * 1000 + ATMOSPHERIC_PRESSURE_PA, pressureUnit: PressureUnit.PAA };
    case PressureUnit.BARG: return { pressure: pressureValue.pressure * 100000 + ATMOSPHERIC_PRESSURE_PA, pressureUnit: PressureUnit.PAA };
    case PressureUnit.PSIG: return { pressure: pressureValue.pressure * 6894.76 + ATMOSPHERIC_PRESSURE_PA, pressureUnit: PressureUnit.PAA };
    
    // Vacuum pressure units - convert to Pa and subtract atmospheric pressure
    case PressureUnit.INHG: return { pressure: (pressureValue.pressure * 3386.39) - ATMOSPHERIC_PRESSURE_PA, pressureUnit: PressureUnit.PAA };
    case PressureUnit.MMHG: return { pressure: (pressureValue.pressure * 133.322) - ATMOSPHERIC_PRESSURE_PA, pressureUnit: PressureUnit.PAA };
 
    default: throw new Error(`Unsupported pressure unit: ${pressureValue.pressureUnit}`);
  }
}

/**
 * Converts pressure from Pascals (absolute) to any supported unit
 * @param pressurePa The pressure value in Pascals (absolute)
 * @param targetUnit The target unit
 * @returns The pressure in the target unit
 */
export function convertPressureFromPa(pressurePa: number, targetUnit: PressureUnit, ATMOSPHERIC_PRESSURE_PA: number = 101325,): PressureValue {
  
  switch (targetUnit) {
    // Absolute pressure units - convert from Pa
    case PressureUnit.PAA: return { pressure: pressurePa, pressureUnit: PressureUnit.PAA };
    case PressureUnit.KPAA: return { pressure: pressurePa / 1000, pressureUnit: PressureUnit.KPAA };
    case PressureUnit.BARA: return { pressure: pressurePa / 100000, pressureUnit: PressureUnit.BARA };
    case PressureUnit.PSIA: return { pressure: pressurePa / 6894.76, pressureUnit: PressureUnit.PSIA };
    
    // Gauge pressure units - convert from Pa and subtract atmospheric pressure
    case PressureUnit.PAG: return { pressure: pressurePa - ATMOSPHERIC_PRESSURE_PA, pressureUnit: PressureUnit.PAG };
    case PressureUnit.KPAG: return { pressure: (pressurePa - ATMOSPHERIC_PRESSURE_PA) / 1000, pressureUnit: PressureUnit.KPAG };
    case PressureUnit.BARG: return { pressure: (pressurePa - ATMOSPHERIC_PRESSURE_PA) / 100000, pressureUnit: PressureUnit.BARG };
    case PressureUnit.PSIG: return { pressure: (pressurePa - ATMOSPHERIC_PRESSURE_PA) / 6894.76, pressureUnit: PressureUnit.PSIG };
    
    // Vacuum pressure units - convert from Pa and add atmospheric pressure
    case PressureUnit.INHG: return { pressure: (pressurePa + ATMOSPHERIC_PRESSURE_PA) / 3386.39, pressureUnit: PressureUnit.INHG };
    case PressureUnit.MMHG: return { pressure: (pressurePa + ATMOSPHERIC_PRESSURE_PA) / 133.322, pressureUnit: PressureUnit.MMHG };
    
    default: throw new Error(`Unsupported pressure unit: ${targetUnit}`);
  }
}

/**
 * Validates if a temperature unit is supported
 * @param unit The unit to validate
 * @returns True if the unit is valid
 */
export function isValidTemperatureUnit(unit: string): unit is TemperatureUnit {
  return Object.values(TemperatureUnit).includes(unit as TemperatureUnit);
}

/**
 * Validates if a pressure unit is supported
 * @param unit The unit to validate
 * @returns True if the unit is valid
 */
export function isValidPressureUnit(unit: string): unit is PressureUnit {
  return Object.values(PressureUnit).includes(unit as PressureUnit);
} 